import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const AdminLogin = () => {
    const { user, login, loading } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    if (loading) return null;
    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate("/admin");
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid credentials");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-[80vh] flex items-center justify-center px-6"
        >
            <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl p-10 shadow-sm hl-fade-up">
                <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-12 h-12 rounded-full bg-[#5B6E4C] text-[#FAF8F5] flex items-center justify-center mb-4">
                        <Lock className="w-5 h-5" />
                    </div>
                    <p className="font-script text-[#C86F53] text-2xl leading-none">back of house —</p>
                    <h1 className="font-serif text-3xl text-stone-900 mt-1">
                        Admin sign in
                    </h1>
                    <p className="text-stone-500 text-sm mt-2">
                        Only for the kitchen crew.
                    </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">
                            Email
                        </label>
                        <input
                            data-testid="admin-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-[#FAF8F5] text-stone-800 focus:outline-none focus:border-[#5B6E4C]"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-stone-600 mb-2">
                            Password
                        </label>
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-[#FAF8F5] text-stone-800 focus:outline-none focus:border-[#5B6E4C]"
                        />
                    </div>

                    {error ? (
                        <p
                            data-testid="admin-login-error"
                            className="text-sm text-[#A85C43] bg-[#C86F53]/10 border border-[#C86F53]/30 px-4 py-3 rounded-lg"
                        >
                            {error}
                        </p>
                    ) : null}

                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3.5 rounded-full bg-[#5B6E4C] text-[#FAF8F5] hover:bg-[#42533A] hl-btn-lift disabled:opacity-50 text-sm uppercase tracking-widest"
                    >
                        {submitting ? "Signing in..." : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;
